import React, { useState, useEffect, useCallback } from "react";
import {
  Bot, Inbox, Settings, Users, Newspaper, FileText, RefreshCw,
  CheckCircle, XCircle, Clock, Loader2, Sparkles, Tag, ShoppingCart,
  TrendingUp, HeartHandshake, Wallet, MessageSquare, Send, Plus, Trash2,
  ShieldCheck, PlayCircle, Mail, Sun, Link2, Moon, CalendarOff
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MODULE_ICON = {
  TrendingUp, Tag, ShoppingCart, Sparkles, HeartHandshake, Users, Wallet,
  Newspaper, MessageSquare,
};

const RISK_TONE = {
  low:    "bg-emerald-50 text-emerald-700 border-emerald-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  high:   "bg-rose-50 text-rose-700 border-rose-200",
};

const MODE_META = {
  off:     { label: "Off",       cls: "bg-slate-200 text-slate-600" },
  suggest: { label: "Suggest",   cls: "bg-indigo-100 text-indigo-700" },
  auto:    { label: "Auto-pilot",cls: "bg-emerald-100 text-emerald-700" },
};

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};


/* ─── Overview Tiles ─────────────────────────────────────────────── */
const Tile = ({ icon: Icon, label, value, sub, tone = "indigo" }) => {
  const tones = {
    indigo:  "bg-indigo-50 border-indigo-100 text-indigo-700",
    pink:    "bg-pink-50 border-pink-100 text-pink-700",
    emerald: "bg-emerald-50 border-emerald-100 text-emerald-700",
    amber:   "bg-amber-50 border-amber-100 text-amber-700",
  }[tone];
  return (
    <div className={`rounded-2xl border p-4 ${tones}`} data-testid={`boss-tile-${label.replace(/\s+/g,'-').toLowerCase()}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14}/>
        <span className="text-[10px] uppercase tracking-wider font-bold opacity-70">{label}</span>
      </div>
      <p className="text-2xl font-black">{value}</p>
      {sub && <p className="text-[10px] opacity-70 mt-0.5">{sub}</p>}
    </div>
  );
};

/* ─── Suggestion Card ────────────────────────────────────────────── */
const SuggestionCard = ({ sug, onAction, busy }) => {
  const Icon = MODULE_ICON[sug.moduleIcon] || Sparkles;
  const riskCls = RISK_TONE[sug.risk] || RISK_TONE.low;
  const [note, setNote] = useState("");
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden" data-testid={`sug-card-${sug.id}`}>
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#E83E8C] to-[#7C3AED] flex items-center justify-center text-white shrink-0">
            <Icon size={18}/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-bold text-slate-900 text-sm">{sug.title}</h4>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${riskCls}`}>{sug.risk} risk</span>
            </div>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">{sug.summary}</p>
            <p className="text-[10px] text-slate-400 mt-2">💡 Impact: {sug.impact}</p>
            {open && sug.context && (
              <pre className="mt-3 text-[10px] bg-slate-50 border border-slate-100 rounded-lg p-2 overflow-auto max-h-56 whitespace-pre-wrap">
                {JSON.stringify(sug.context, null, 2)}
              </pre>
            )}
          </div>
        </div>
      </div>
      <div className="px-4 py-3 bg-slate-50 border-t border-slate-100 flex items-center gap-2 flex-wrap">
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional note…"
          className="flex-1 min-w-[120px] text-xs px-3 py-1.5 rounded-lg border border-slate-200 bg-white"
          data-testid={`sug-note-${sug.id}`}
        />
        <button
          onClick={() => setOpen(!open)}
          className="text-[11px] font-semibold px-3 py-1.5 rounded-lg text-slate-600 hover:bg-slate-100"
          data-testid={`sug-details-${sug.id}`}
        >
          {open ? "Hide" : "Details"}
        </button>
        <button
          onClick={() => onAction(sug.id, "reject", note)}
          disabled={busy}
          className="text-[11px] font-semibold px-3 py-1.5 rounded-lg text-rose-600 hover:bg-rose-50 disabled:opacity-50"
          data-testid={`sug-reject-${sug.id}`}
        >
          <XCircle size={13} className="inline mr-1"/>Reject
        </button>
        <button
          onClick={() => onAction(sug.id, "approve", note)}
          disabled={busy}
          className="text-[11px] font-bold px-4 py-1.5 rounded-lg bg-gradient-to-r from-[#E83E8C] to-[#7C3AED] text-white hover:shadow-md disabled:opacity-50"
          data-testid={`sug-approve-${sug.id}`}
        >
          {busy ? <Loader2 size={13} className="inline animate-spin mr-1"/> : <CheckCircle size={13} className="inline mr-1"/>}
          Approve & Execute
        </button>
      </div>
    </div>
  );
};


/* ─── Autonomy Settings Panel ───────────────────────────────────── */
const AutonomyPanel = ({ modules, autonomy, onChange }) => (
  <div className="space-y-2">
    {modules.map(m => {
      const Icon = MODULE_ICON[m.icon] || Sparkles;
      const mode = autonomy[m.id] || m.default_mode;
      return (
        <div key={m.id} className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-white" data-testid={`autonomy-row-${m.id}`}>
          <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600">
            <Icon size={16}/>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-slate-900">{m.label}</p>
            <p className="text-[11px] text-slate-500 truncate">{m.desc}</p>
          </div>
          <div className="flex gap-1 shrink-0">
            {["off","suggest","auto"].map(k => (
              <button
                key={k}
                onClick={() => onChange(m.id, k)}
                className={`text-[10px] font-semibold px-2.5 py-1 rounded-md border transition-all ${
                  mode === k ? MODE_META[k].cls + " border-transparent" : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
                }`}
                data-testid={`autonomy-${m.id}-${k}`}
              >
                {MODE_META[k].label}
              </button>
            ))}
          </div>
        </div>
      );
    })}
  </div>
);


/* ─── Team Pulse Board ──────────────────────────────────────────── */
const TeamPulseBoard = ({ authAxios }) => {
  const [data, setData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [newQ, setNewQ] = useState("");
  const [newQRoles, setNewQRoles] = useState("*");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        authAxios.get(`${API_URL}/api/doozyboss/pulse/today`),
        authAxios.get(`${API_URL}/api/doozyboss/pulse/questions`),
      ]);
      setData(r1.data);
      setQuestions(r2.data.items || []);
    } finally { setLoading(false); }
  }, [authAxios]);

  useEffect(() => { load(); }, [load]);

  const run = async (path) => {
    setBusy(true);
    try {
      await authAxios.post(`${API_URL}/api/doozyboss/pulse/${path}`);
      await load();
    } finally { setBusy(false); }
  };

  const addQ = async () => {
    if (!newQ.trim()) return;
    await authAxios.post(`${API_URL}/api/doozyboss/pulse/questions`, {
      text: newQ.trim(),
      role_tags: newQRoles.split(",").map(r => r.trim()).filter(Boolean),
    });
    setNewQ("");
    setNewQRoles("*");
    await load();
  };

  const delQ = async (id) => {
    if (!window.confirm("Delete this question?")) return;
    await authAxios.delete(`${API_URL}/api/doozyboss/pulse/questions/${id}`);
    await load();
  };

  if (!data) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap opacity-60 pointer-events-none">
          <div className="rounded-xl bg-gradient-to-r from-[#E83E8C]/10 to-[#7C3AED]/10 px-4 py-2 border border-pink-100">
            <span className="text-xs text-slate-500">Loading pulse…</span>
          </div>
          <button disabled className="text-[11px] font-semibold px-3 py-2 rounded-lg bg-indigo-600/40 text-white flex items-center gap-1"><Send size={13}/> Send pulse now</button>
          <button disabled className="text-[11px] font-semibold px-3 py-2 rounded-lg bg-amber-500/40 text-white flex items-center gap-1"><Clock size={13}/> Nudge silent</button>
          <button disabled className="text-[11px] font-semibold px-3 py-2 rounded-lg bg-emerald-600/40 text-white flex items-center gap-1"><Mail size={13}/> Send summary</button>
        </div>
        <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-pink-500" size={28}/></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="rounded-xl bg-gradient-to-r from-[#E83E8C]/10 to-[#7C3AED]/10 px-4 py-2 border border-pink-100">
          <span className="text-xs text-slate-600">Today · </span>
          <span className="text-sm font-black text-[#7C3AED]">{data.answered}/{data.total}</span>
          <span className="text-xs text-slate-600"> responded</span>
        </div>
        <button onClick={() => run("run")} disabled={busy} className="text-[11px] font-semibold px-3 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1" data-testid="pulse-run-btn">
          <Send size={13}/> Send pulse now
        </button>
        <button onClick={() => run("nudge")} disabled={busy} className="text-[11px] font-semibold px-3 py-2 rounded-lg bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 flex items-center gap-1" data-testid="pulse-nudge-btn">
          <Clock size={13}/> Nudge silent
        </button>
        <button onClick={() => run("summary")} disabled={busy} className="text-[11px] font-semibold px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1" data-testid="pulse-summary-btn">
          <Mail size={13}/> Send summary to boss
        </button>
        <button onClick={load} className="text-[11px] font-semibold px-3 py-2 rounded-lg text-slate-600 hover:bg-slate-100 flex items-center gap-1 ml-auto" data-testid="pulse-refresh-btn">
          <RefreshCw size={13}/> Refresh
        </button>
      </div>

      {data.summary && (
        <div className="rounded-2xl border border-emerald-100 bg-emerald-50/50 p-4" data-testid="pulse-summary-card">
          <h4 className="text-xs font-bold text-emerald-700 mb-2">Today's pulse summary</h4>
          <pre className="text-xs text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">{data.summary.summary}</pre>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {data.checkins.length === 0 && (
          <div className="col-span-full text-center py-10 text-slate-500 text-sm">
            No pulse sent today yet. Click "Send pulse now" to ping the team.
          </div>
        )}
        {data.checkins.map(c => (
          <div key={c.id} className={`rounded-xl border p-3 ${c.answered ? "bg-emerald-50/50 border-emerald-100" : "bg-white border-slate-200"}`} data-testid={`pulse-checkin-${c.id}`}>
            <div className="flex items-center gap-2 mb-1">
              <div className={`w-2 h-2 rounded-full ${c.answered ? "bg-emerald-500" : "bg-amber-400"}`}/>
              <span className="font-bold text-slate-900 text-sm">{c.user_name}</span>
              <span className="text-[10px] text-slate-400 uppercase">{c.user_role}</span>
              {c.nudges > 0 && <span className="text-[10px] text-amber-600">· nudged {c.nudges}×</span>}
            </div>
            <p className="text-[11px] text-slate-500 italic">"{c.question_text}"</p>
            {c.answered ? (
              <p className="text-xs text-slate-800 mt-2 bg-white/60 rounded-lg px-2 py-1.5">{c.answer}</p>
            ) : (
              <p className="text-[10px] text-slate-400 mt-2">awaiting reply…</p>
            )}
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <h4 className="text-sm font-bold text-slate-900 mb-2 flex items-center gap-2"><ShieldCheck size={14} className="text-indigo-500"/> Question library</h4>
        <div className="flex items-center gap-2 mb-3">
          <input value={newQ} onChange={(e) => setNewQ(e.target.value)} placeholder="New question (use {name} placeholder)" className="flex-1 text-xs px-3 py-2 rounded-lg border border-slate-200" data-testid="new-pulse-q-text"/>
          <input value={newQRoles} onChange={(e) => setNewQRoles(e.target.value)} placeholder="roles (comma-sep, * for all)" className="w-48 text-xs px-3 py-2 rounded-lg border border-slate-200" data-testid="new-pulse-q-roles"/>
          <button onClick={addQ} className="text-xs font-bold px-3 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 flex items-center gap-1" data-testid="new-pulse-q-add">
            <Plus size={13}/> Add
          </button>
        </div>
        <div className="space-y-1 max-h-60 overflow-y-auto">
          {questions.map(q => (
            <div key={q.id} className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg hover:bg-slate-50">
              <span className="text-[10px] font-bold text-slate-400 uppercase">{(q.role_tags || []).join(",")}</span>
              <span className="flex-1 text-slate-700 truncate">{q.text}</span>
              <button onClick={() => delQ(q.id)} className="text-slate-400 hover:text-rose-600" data-testid={`pulse-q-del-${q.id}`}><Trash2 size={13}/></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};


/* ─── Audit Log ─────────────────────────────────────────────────── */
const AuditLog = ({ authAxios }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authAxios.get(`${API_URL}/api/doozyboss/audit`).then(r => {
      setItems(r.data.items || []);
      setLoading(false);
    });
  }, [authAxios]);

  if (loading) return <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-pink-500" size={28}/></div>;
  if (!items.length) return <div className="text-center py-10 text-slate-500 text-sm">No actions executed yet.</div>;

  return (
    <div className="space-y-1">
      {items.map(a => (
        <div key={a.id} className="flex items-start gap-3 text-xs px-3 py-2 rounded-lg hover:bg-slate-50 border-b border-slate-50" data-testid={`audit-row-${a.id}`}>
          <span className={`w-2 h-2 rounded-full mt-1.5 ${a.result?.ok ? "bg-emerald-500" : "bg-rose-500"}`}/>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-slate-800">{a.module} · {a.action}</p>
            <p className="text-[11px] text-slate-500 truncate">{a.result?.message || "—"}</p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-[10px] text-slate-400">{fmt(a.created_at)}</p>
            <p className="text-[10px] text-indigo-500 font-semibold">by {a.actor}</p>
          </div>
        </div>
      ))}
    </div>
  );
};


/* ─── Weekly Brief Panel ────────────────────────────────────────── */
const BriefPanel = ({ authAxios }) => {
  const [briefs, setBriefs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const r = await authAxios.get(`${API_URL}/api/doozyboss/briefs/recent`);
    setBriefs(r.data.items || []);
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setBusy(true);
    try {
      await authAxios.post(`${API_URL}/api/doozyboss/run/weekly-brief`);
      await load();
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <button onClick={runNow} disabled={busy} className="text-xs font-bold px-4 py-2 rounded-lg bg-gradient-to-r from-[#E83E8C] to-[#7C3AED] text-white hover:shadow-md disabled:opacity-50 flex items-center gap-1" data-testid="brief-run-btn">
          {busy ? <Loader2 size={13} className="animate-spin"/> : <PlayCircle size={13}/>}
          Draft + send now
        </button>
        <p className="text-[11px] text-slate-500">Scheduled: Monday 7 AM IST · WA + Email</p>
      </div>
      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-pink-500" size={24}/></div>
      ) : briefs.length === 0 ? (
        <p className="text-center py-10 text-slate-500 text-sm">No briefs yet. Click "Draft + send now" to generate the first one.</p>
      ) : (
        briefs.map(b => (
          <div key={b.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`brief-${b.id}`}>
            <div className="flex items-center gap-2 mb-2">
              <Newspaper size={14} className="text-[#7C3AED]"/>
              <span className="text-xs font-bold text-slate-700">Week ending {b.week_ending}</span>
              <span className="text-[10px] text-slate-400 ml-auto">{fmt(b.generated_at)}</span>
            </div>
            <pre className="text-xs text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">{b.summary}</pre>
          </div>
        ))
      )}
    </div>
  );
};


/* ─── Whisper Panel ─────────────────────────────────────────────── */
const WhisperPanel = ({ authAxios }) => {
  const [whisper, setWhisper] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authAxios.get(`${API_URL}/api/doozyboss/whisper/today`);
      setWhisper(r.data.whisper);
    } finally { setLoading(false); }
  }, [authAxios]);

  useEffect(() => { load(); }, [load]);

  const sendNow = async () => {
    setBusy(true);
    try {
      await authAxios.post(`${API_URL}/api/doozyboss/whisper/run`);
      await load();
    } finally { setBusy(false); }
  };

  const reply = async (action) => {
    setBusy(true);
    try {
      const r = await authAxios.post(`${API_URL}/api/doozyboss/whisper/reply`, { action });
      await load();
      if (action === "YES" && r.data.results) {
        alert(`Ran ${r.data.results.filter(x => x.ok).length}/${r.data.results.length} actions.`);
      }
    } catch (e) {
      alert(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="rounded-2xl bg-gradient-to-br from-[#E83E8C]/10 via-white to-[#7C3AED]/10 border border-pink-100 p-5 opacity-80">
          <div className="flex items-center gap-2 mb-2">
            <Sun size={18} className="text-amber-500"/>
            <h3 className="font-black text-slate-900">Morning Whisper</h3>
          </div>
          <p className="text-xs text-slate-500">Loading today's whisper…</p>
          <div className="flex items-center gap-2 mt-3">
            <button disabled className="text-xs font-bold px-4 py-2 rounded-lg bg-gradient-to-r from-[#E83E8C]/60 to-[#7C3AED]/60 text-white flex items-center gap-1" data-testid="whisper-run-btn">
              <Loader2 size={13} className="animate-spin"/> Draft + send today's whisper
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-gradient-to-br from-[#E83E8C]/10 via-white to-[#7C3AED]/10 border border-pink-100 p-5" data-testid="whisper-header">
        <div className="flex items-center gap-2 mb-2">
          <Sun size={18} className="text-amber-500"/>
          <h3 className="font-black text-slate-900">Morning Whisper</h3>
          <span className="text-[10px] font-semibold text-white px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-400 to-pink-500 ml-1">07:45 IST · WA + EMAIL</span>
        </div>
        <p className="text-xs text-slate-600">A single WA every morning with the top 3 moves I'd auto-execute today. Tap each link or reply <b>YES</b> to let all three go.</p>
        <div className="flex items-center gap-2 mt-3">
          <button onClick={sendNow} disabled={busy} className="text-xs font-bold px-4 py-2 rounded-lg bg-gradient-to-r from-[#E83E8C] to-[#7C3AED] text-white hover:shadow-md disabled:opacity-50 flex items-center gap-1" data-testid="whisper-run-btn">
            {busy ? <Loader2 size={13} className="animate-spin"/> : <Send size={13}/>}
            Draft + send today's whisper
          </button>
          <button onClick={load} className="text-xs font-semibold px-3 py-2 rounded-lg text-slate-600 hover:bg-slate-100 flex items-center gap-1" data-testid="whisper-refresh-btn">
            <RefreshCw size={13}/> Refresh
          </button>
        </div>
      </div>

      {!whisper ? (
        <div className="text-center py-14 rounded-2xl border-2 border-dashed border-slate-200">
          <Sun className="mx-auto mb-3 text-slate-300" size={36}/>
          <p className="text-sm text-slate-500">No whisper yet today. Click "Draft + send" to generate one now.</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="whisper-card">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] font-bold uppercase text-slate-400">Generated</span>
            <span className="text-[10px] text-slate-600">{fmt(whisper.generated_at)}</span>
            <span className="ml-auto text-[10px] text-emerald-700 font-semibold">{(whisper.sent_via || []).join(" · ") || "pending"}</span>
          </div>
          <pre className="text-xs text-slate-700 whitespace-pre-wrap font-sans leading-relaxed bg-slate-50 border border-slate-100 rounded-lg p-3">{whisper.body}</pre>
          {whisper.boss_reply ? (
            <div className="mt-3 text-xs text-slate-500">Boss replied: <b className="text-[#7C3AED]">{whisper.boss_reply}</b></div>
          ) : (
            <div className="mt-3 flex items-center gap-2">
              <button onClick={() => reply("YES")} disabled={busy} className="text-xs font-bold px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1" data-testid="whisper-yes-btn">
                <CheckCircle size={13}/> YES — run all 3
              </button>
              <button onClick={() => reply("SKIP")} disabled={busy} className="text-xs font-semibold px-3 py-2 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50" data-testid="whisper-skip-btn">
                SKIP today
              </button>
              <span className="text-[10px] text-slate-500 ml-2 flex items-center gap-1">
                <Link2 size={11}/> or tap the per-item links in the message to approve one at a time
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


/* ─── Quiet Hours Panel ─────────────────────────────────────────── */
const QuietHoursPanel = ({ authAxios, onChange }) => {
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newVac, setNewVac] = useState({ from: "", to: "", note: "" });

  const load = useCallback(async () => {
    const r = await authAxios.get(`${API_URL}/api/doozyboss/quiet-hours`);
    setState(r.data);
  }, [authAxios]);

  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await authAxios.post(`${API_URL}/api/doozyboss/quiet-hours`, patch);
      setState(r.data);
      onChange && onChange(r.data);
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const addVacation = async () => {
    if (!newVac.from || !newVac.to) return;
    const ranges = [...(state.config.vacation_ranges || []), newVac];
    await save({ vacation_ranges: ranges });
    setNewVac({ from: "", to: "", note: "" });
  };

  const removeVacation = async (i) => {
    const ranges = (state.config.vacation_ranges || []).filter((_, idx) => idx !== i);
    await save({ vacation_ranges: ranges });
  };

  if (!state) return <div className="py-8 flex justify-center"><Loader2 className="animate-spin text-indigo-500" size={20}/></div>;
  const cfg = state.config;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="quiet-hours-panel">
      <div className="flex items-center gap-2 mb-3">
        <Moon size={16} className="text-indigo-500"/>
        <h4 className="text-sm font-bold text-slate-900">Quiet Hours</h4>
        {state.is_quiet_now ? (
          <span className="text-[10px] font-bold text-white px-2 py-0.5 rounded-full bg-indigo-600" data-testid="quiet-now-badge">QUIET NOW</span>
        ) : (
          <span className="text-[10px] font-bold text-emerald-700 px-2 py-0.5 rounded-full bg-emerald-100" data-testid="active-now-badge">ACTIVE</span>
        )}
        <span className="text-[10px] text-slate-400 ml-1 truncate">{state.reason}</span>
      </div>
      <p className="text-[11px] text-slate-500 mb-3">
        Pauses WA pings, summaries, whispers and auto-pilot execution. Suggestions keep piling up in the inbox — nothing is lost.
        The moment quiet ends, you'll get a single <b>morning catch-up</b> WA with everything that accumulated.
      </p>

      <div className="flex items-center gap-2 mb-3">
        <button
          onClick={async () => {
            setSaving(true);
            try {
              const r = await authAxios.post(`${API_URL}/api/doozyboss/quiet-hours/send-digest-now`);
              alert(r.data.sent ? "Morning catch-up sent." : "Nothing accumulated — skipped.");
            } finally { setSaving(false); }
          }}
          disabled={saving}
          className="text-[11px] font-bold px-3 py-1.5 rounded-lg bg-gradient-to-r from-amber-400 to-pink-500 text-white hover:shadow-md disabled:opacity-50 flex items-center gap-1"
          data-testid="qh-digest-now-btn"
        >
          <Sun size={12}/> Send catch-up digest now
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Master toggle */}
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input type="checkbox" checked={cfg.enabled} onChange={e => save({ enabled: e.target.checked })}
                 disabled={saving} data-testid="qh-enabled-toggle"/>
          <span className="font-semibold">Quiet Hours enabled</span>
        </label>

        {/* Weekends */}
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input type="checkbox" checked={cfg.weekends_full} onChange={e => save({ weekends_full: e.target.checked })}
                 disabled={saving} data-testid="qh-weekends-toggle"/>
          <span className="font-semibold">Silence all weekends</span>
        </label>

        {/* Daily window */}
        <div className="flex items-center gap-2 text-xs">
          <span className="text-slate-500">Every night:</span>
          <input type="time" value={cfg.daily_start} onChange={e => save({ daily_start: e.target.value })}
                 disabled={saving || !cfg.enabled}
                 className="text-xs px-2 py-1 rounded border border-slate-200"
                 data-testid="qh-daily-start"/>
          <span className="text-slate-400">→</span>
          <input type="time" value={cfg.daily_end} onChange={e => save({ daily_end: e.target.value })}
                 disabled={saving || !cfg.enabled}
                 className="text-xs px-2 py-1 rounded border border-slate-200"
                 data-testid="qh-daily-end"/>
          <span className="text-slate-400">IST</span>
        </div>
      </div>

      {/* Vacations */}
      <div className="mt-4">
        <div className="flex items-center gap-2 mb-2">
          <CalendarOff size={12} className="text-indigo-500"/>
          <span className="text-xs font-bold text-slate-700">Vacation ranges</span>
        </div>
        {(cfg.vacation_ranges || []).length === 0 && (
          <p className="text-[10px] text-slate-400 mb-2">No vacations scheduled.</p>
        )}
        {(cfg.vacation_ranges || []).map((r, i) => (
          <div key={`${r.from}-${r.to}-${i}`} className="flex items-center gap-2 text-xs px-2 py-1.5 rounded-lg hover:bg-slate-50" data-testid={`qh-vac-row-${i}`}>
            <span className="font-mono text-slate-700">{r.from} → {r.to}</span>
            <span className="text-slate-500 flex-1 truncate">{r.note || "—"}</span>
            <button onClick={() => removeVacation(i)} disabled={saving} className="text-slate-400 hover:text-rose-600" data-testid={`qh-vac-del-${i}`}><Trash2 size={12}/></button>
          </div>
        ))}
        <div className="flex items-center gap-2 mt-2">
          <input type="date" value={newVac.from} onChange={e => setNewVac({ ...newVac, from: e.target.value })}
                 className="text-xs px-2 py-1 rounded border border-slate-200" data-testid="qh-vac-from"/>
          <input type="date" value={newVac.to} onChange={e => setNewVac({ ...newVac, to: e.target.value })}
                 className="text-xs px-2 py-1 rounded border border-slate-200" data-testid="qh-vac-to"/>
          <input type="text" value={newVac.note} onChange={e => setNewVac({ ...newVac, note: e.target.value })}
                 placeholder="e.g. Diwali trip" className="flex-1 text-xs px-2 py-1 rounded border border-slate-200" data-testid="qh-vac-note"/>
          <button onClick={addVacation} disabled={saving || !newVac.from || !newVac.to}
                  className="text-[11px] font-bold px-3 py-1 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 flex items-center gap-1"
                  data-testid="qh-vac-add">
            <Plus size={11}/> Add
          </button>
        </div>
      </div>
    </div>
  );
};


/* ─── Main Tab ──────────────────────────────────────────────────── */
const DoozyBossTab = ({ authAxios }) => {
  const [status, setStatus] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [autonomy, setAutonomy] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("inbox");
  const [busyId, setBusyId] = useState(null);
  const [generating, setGenerating] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [st, sugs, aut] = await Promise.all([
        authAxios.get(`${API_URL}/api/doozyboss/status`),
        authAxios.get(`${API_URL}/api/doozyboss/suggestions?status=pending`),
        authAxios.get(`${API_URL}/api/doozyboss/autonomy`),
      ]);
      setStatus(st.data);
      const moduleIconById = Object.fromEntries((st.data.modules || []).map(m => [m.id, m.icon]));
      setSuggestions((sugs.data.items || []).map(s => ({ ...s, moduleIcon: moduleIconById[s.module] })));
      setAutonomy(aut.data.autonomy || {});
    } finally { setLoading(false); }
  }, [authAxios]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const generate = async () => {
    setGenerating(true);
    try {
      await authAxios.post(`${API_URL}/api/doozyboss/run/generate`);
      await loadAll();
    } finally { setGenerating(false); }
  };

  const onSuggAction = async (id, kind, note) => {
    setBusyId(id);
    try {
      await authAxios.post(`${API_URL}/api/doozyboss/suggestions/${id}/${kind}`, { note });
      await loadAll();
    } finally { setBusyId(null); }
  };

  const setMode = async (moduleId, mode) => {
    await authAxios.post(`${API_URL}/api/doozyboss/autonomy`, { module: moduleId, mode });
    setAutonomy(a => ({ ...a, [moduleId]: mode }));
  };

  if (loading || !status) {
    return <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-pink-500" size={32}/></div>;
  }

  const TABS = [
    { id: "inbox",     label: "Action Inbox",    icon: Inbox,         badge: status.pending_total },
    { id: "whisper",   label: "Morning Whisper", icon: Sun,           badge: 0 },
    { id: "pulse",     label: "Team Pulse",      icon: MessageSquare, badge: status.pulse.total - status.pulse.answered },
    { id: "brief",     label: "Weekly Brief",    icon: Newspaper,     badge: 0 },
    { id: "autonomy",  label: "Autonomy",        icon: Settings,      badge: 0 },
    { id: "audit",     label: "Audit Log",       icon: FileText,      badge: 0 },
  ];

  return (
    <div className="space-y-6 p-1" data-testid="doozyboss-tab">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#E83E8C] to-[#7C3AED] flex items-center justify-center text-white shadow-lg">
          <Bot size={22}/>
        </div>
        <div>
          <h2 className="text-lg font-black text-slate-900 flex items-center gap-2">
            DoozyBoss <span className="text-[10px] font-semibold text-white px-2 py-0.5 rounded-full bg-gradient-to-r from-[#E83E8C] to-[#7C3AED]">GROWTH AGENT</span>
          </h2>
          <p className="text-xs text-slate-500">Your 1-click growth & ops commander. Approve or let it run.</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {status.quiet?.active && (
            <span className="flex items-center gap-1 text-[10px] font-bold text-white px-2.5 py-1 rounded-full bg-indigo-600" title={status.quiet.reason} data-testid="boss-quiet-badge">
              <Moon size={11}/> Quiet Hours
            </span>
          )}
          <button onClick={generate} disabled={generating} className="text-xs font-bold px-4 py-2 rounded-lg bg-gradient-to-r from-[#E83E8C] to-[#7C3AED] text-white hover:shadow-md disabled:opacity-50 flex items-center gap-1" data-testid="boss-generate-btn">
            {generating ? <Loader2 size={13} className="animate-spin"/> : <Sparkles size={13}/>}
            Scan now
          </button>
          <button onClick={loadAll} className="text-xs font-semibold px-3 py-2 rounded-lg text-slate-600 hover:bg-slate-100 flex items-center gap-1" data-testid="boss-refresh-btn">
            <RefreshCw size={13}/> Refresh
          </button>
        </div>
      </div>

      {/* Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile icon={Inbox}         label="Pending"        value={status.pending_total}      sub="awaiting approval" tone="indigo"/>
        <Tile icon={CheckCircle}   label="Done Today"     value={status.executed_today}     sub="actions executed"  tone="emerald"/>
        <Tile icon={XCircle}       label="Failed Today"   value={status.failed_today}       sub={status.failed_today > 0 ? "check audit log" : "all clean"} tone={status.failed_today > 0 ? "amber" : "emerald"}/>
        <Tile icon={MessageSquare} label="Team Pulse"     value={`${status.pulse.answered}/${status.pulse.total}`} sub="responded today"  tone="pink"/>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 overflow-x-auto border-b border-slate-200">
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-1.5 text-xs font-semibold px-4 py-2.5 border-b-2 transition-all whitespace-nowrap ${active ? "border-[#E83E8C] text-[#E83E8C]" : "border-transparent text-slate-500 hover:text-slate-800"}`} data-testid={`boss-tab-${t.id}`}>
              <Icon size={14}/> {t.label}
              {t.badge > 0 && <span className="text-[9px] font-bold bg-[#E83E8C] text-white rounded-full px-1.5 py-0.5 ml-0.5">{t.badge}</span>}
            </button>
          );
        })}
      </div>

      {/* Tab body */}
      {tab === "inbox" && (
        <div className="space-y-3">
          {suggestions.length === 0 ? (
            <div className="text-center py-14 rounded-2xl border-2 border-dashed border-slate-200">
              <Sparkles className="mx-auto mb-3 text-slate-300" size={36}/>
              <p className="text-sm text-slate-500">Inbox is clear. Click <b>Scan now</b> to generate fresh suggestions.</p>
            </div>
          ) : (
            suggestions.map(s => (
              <SuggestionCard key={s.id} sug={s} onAction={onSuggAction} busy={busyId === s.id}/>
            ))
          )}
        </div>
      )}

      {tab === "pulse" && <TeamPulseBoard authAxios={authAxios}/>}
      {tab === "brief" && <BriefPanel authAxios={authAxios}/>}
      {tab === "autonomy" && (
        <div className="space-y-4">
          <QuietHoursPanel authAxios={authAxios} onChange={loadAll}/>
          <AutonomyPanel modules={status.modules} autonomy={autonomy} onChange={setMode}/>
        </div>
      )}
      {tab === "audit" && <AuditLog authAxios={authAxios}/>}
    </div>
  );
};

export default DoozyBossTab;
